import { json } from "@codemirror/lang-json";
import {
  foldGutter,
  HighlightStyle,
  syntaxHighlighting,
  toggleFold,
} from "@codemirror/language";
import { lintGutter } from "@codemirror/lint";
import { type Extension, Prec } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  keymap,
  ViewPlugin,
  type ViewUpdate,
} from "@codemirror/view";
import { tags as t } from "@lezer/highlight";
import { indentationMarkers } from "@replit/codemirror-indentation-markers";
import { designVariables } from "@studio3t/design-variables";
import {
  GenericDropdown,
  type GenericDropdownItem,
  Icon,
  IconSVG,
  SVGColorKey,
} from "@studio3t/generic-components";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { useCallback, useMemo, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import styled, {
  css,
  type DefaultTheme,
  ThemeProvider,
  useTheme,
} from "styled-components";
import { wrapIndentCh } from "./wrapIndent";

// Editor font stack, shared with the Studio editor-panel states.
export const EDITOR_FONT =
  '"Geist Mono", "JetBrains Mono", "Fira Code", "Monaco", "Consolas", monospace';
// Figma: 14px text on 24px rows (each line is py-5 around 14px text).
const FONT_SIZE = 14;
const LINE_HEIGHT = 24;

// Fold marker using the design-system Chevron icon (rendered to static SVG so
// CodeMirror's markerDOM can mount it). The icon points right by default, so
// rotate it down when the block is expanded.
function chevronMarker(markup: string, open: boolean): HTMLElement {
  const el = document.createElement("span");
  el.style.display = "inline-flex";
  el.style.alignItems = "center";
  el.style.justifyContent = "center";
  el.style.transform = open ? "rotate(90deg)" : "none";
  el.innerHTML = markup;
  return el;
}

const IS_MAC =
  typeof navigator !== "undefined" &&
  (navigator.platform ?? "").toUpperCase().includes("MAC");
const KEY_COPY = IS_MAC ? "⌘C" : "Ctrl+C";

// Same tag → design-system token mapping as Mario's JSON viewer. The trailing
// rules only fire for non-JSON languages (Rhai): keywords, type names and
// builtins land on the same Editor* palette.
function jsonHighlightStyle(theme: DefaultTheme): HighlightStyle {
  return HighlightStyle.define([
    { tag: t.propertyName, color: theme.EditorKey },
    { tag: [t.string, t.special(t.string)], color: theme.EditorString },
    { tag: t.number, color: theme.EditorNumber },
    { tag: t.bool, color: theme.EditorBoolean },
    { tag: t.null, color: theme.EditorNull },
    { tag: t.brace, color: theme.EditorObjectBracket },
    { tag: t.squareBracket, color: theme.EditorArrayBracket },
    { tag: [t.separator, t.contentSeparator], color: theme.EditorSeparator },
    { tag: t.comment, color: theme.EditorComment },
    { tag: t.operator, color: theme.EditorFunction },
    { tag: t.atom, color: theme.EditorKeyword },
    { tag: t.keyword, color: theme.EditorKeyword },
    { tag: t.typeName, color: theme.EditorKey },
    { tag: t.standard(t.variableName), color: theme.EditorFunction },
  ]);
}

// Design-system Chevron rendered to static SVG markup for foldGutter's markerDOM.
function chevronMarkup(theme: DefaultTheme): string {
  return renderToStaticMarkup(
    <ThemeProvider theme={theme}>
      <IconSVG
        icon={Icon.Chevron}
        size={10}
        svgColorKey={SVGColorKey.semanticTextPlaceholder}
        colorHex={theme.editorLineNumTxt}
      />
    </ThemeProvider>,
  );
}

function editorTheme(theme: DefaultTheme) {
  return EditorView.theme(
    {
      "&": {
        backgroundColor: theme.editorBackground,
        color: theme.editorText,
        fontSize: `${FONT_SIZE}px`,
      },
      "&.cm-focused": { outline: "none" },
      // Figma codeZone padding (pt-5 / pb-10); 24px line rows.
      ".cm-content": {
        fontFamily: EDITOR_FONT,
        padding: "5px 0 10px",
        lineHeight: `${LINE_HEIGHT}px`,
      },
      ".cm-line": { lineHeight: `${LINE_HEIGHT}px` },
      ".cm-scroller": {
        fontFamily: EDITOR_FONT,
        lineHeight: `${LINE_HEIGHT}px`,
        // The mono font collapses "..." into a one-cell ellipsis ligature,
        // visually cramping runs of dots (e.g. the schema placeholder's
        // "..." / [...]) — keep every character on its own cell.
        fontVariantLigatures: "none",
      },
      // Gutter sized to match Figma: 33px-wide line-count column with
      // centered numbers, and a fold column holding the 10px chevron.
      ".cm-gutters": {
        backgroundColor: theme.editorLineNumBackground,
        color: theme.editorLineNumTxt,
        border: "none",
        lineHeight: `${LINE_HEIGHT}px`,
      },
      ".cm-gutterElement": { lineHeight: `${LINE_HEIGHT}px` },
      ".cm-lineNumbers .cm-gutterElement": {
        minWidth: "33px",
        padding: "0 2px",
        boxSizing: "border-box",
        textAlign: "center",
        color: theme.editorLineNumTxt,
      },
      ".cm-foldGutter": { width: "14px" },
      // Fixed-width lint column (default sizing lands on a fractional
      // ~19.6px) so the sidebar total is a stable constant everywhere.
      ".cm-gutter-lint": { width: "20px" },
      ".cm-foldGutter .cm-gutterElement": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 2px",
        color: theme.editorLineNumTxt,
        cursor: "pointer",
      },
      ".cm-activeLine": { backgroundColor: "#0000000d" },
      ".cm-activeLineGutter": {
        backgroundColor: "transparent",
        color: theme.editorLineNumTxt,
      },
      ".cm-cursor": { borderLeftColor: theme.editorText },
      // Autosuggestion rows: the same hover feedback as the app's list rows
      // (explorer, stage rows), alongside CodeMirror's own keyboard
      // selection highlight (aria-selected keeps its look under the mouse).
      ".cm-tooltip-autocomplete ul li": { cursor: "pointer" },
      ".cm-tooltip-autocomplete ul li:hover:not([aria-selected])": {
        backgroundColor: theme.sidebarListItemActiveBackground,
      },
      "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection":
        {
          backgroundColor: "#0066cc33",
        },
    },
    { dark: false },
  );
}

// Shared CodeMirror template ("Raw configuration" modal look): used by this
// read-only viewer and by the Studio editors. `language` swaps the JSON
// support for another language (e.g. Rhai in the transform editor).
// Wrapped continuations hang under their line's own indentation (the
// CodeMirror styling recipe: pad the line by its leading whitespace and
// pull the FIRST visual row back with a negative text-indent). Without
// it a deeply indented long line — a UDF service's inline script in the
// Raw configuration JSON — wraps flush left and reads skewed.
const wrapIndentPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = wrapIndentDeco(view);
    }
    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = wrapIndentDeco(update.view);
      }
    }
  },
  { decorations: (plugin) => plugin.decorations },
);

function wrapIndentDeco(view: EditorView): DecorationSet {
  const decorations = [];
  const tabSize = view.state.tabSize;
  for (const { from, to } of view.visibleRanges) {
    for (let pos = from; pos <= to; ) {
      const line = view.state.doc.lineAt(pos);
      const indent = wrapIndentCh(line.text, tabSize);
      if (indent > 0) {
        decorations.push(
          Decoration.line({
            attributes: {
              style: `text-indent:-${indent}ch;padding-left:${indent}ch`,
            },
          }).range(line.from),
        );
      }
      pos = line.to + 1;
    }
  }
  return Decoration.set(decorations);
}

export function buildExtensions(
  theme: DefaultTheme,
  {
    lineWrapping = true,
    language,
    highlight,
    folding = true,
  }: {
    lineWrapping?: boolean;
    language?: Extension;
    /** Replaces the default JSON highlight palette (e.g. the Monaco-parity
     *  Rhai style). */
    highlight?: HighlightStyle;
    /** The fold-chevron gutter; off for bare-code surfaces like the schema
     *  hover preview (Figma Bridge_DocPreview shows no gutter at all). */
    folding?: boolean;
  } = {},
) {
  const markup = chevronMarkup(theme);
  return [
    language ?? json(),
    ...(lineWrapping ? [EditorView.lineWrapping, wrapIndentPlugin] : []),
    syntaxHighlighting(highlight ?? jsonHighlightStyle(theme)),
    // Vertical indent-guide lines (Figma "Editor/indentLine/default" #e2e2e2).
    indentationMarkers({
      highlightActiveBlock: false,
      thickness: 1,
      colors: {
        light: "#e2e2e2",
        dark: "#e2e2e2",
        activeLight: "#e2e2e2",
        activeDark: "#e2e2e2",
      },
    }),
    // Design-system Chevron instead of CodeMirror's default fold caret.
    // The lint column rides along even for editors without a linter, so
    // every editor's sidebar is the same width whether or not an error
    // icon can ever appear; bare-code surfaces (folding: false) skip both.
    ...(folding
      ? [
          foldGutter({ markerDOM: (open) => chevronMarker(markup, open) }),
          lintGutter(),
        ]
      : []),
    editorTheme(theme),
    // Escape, once nothing else wants it, takes focus out of the editor:
    // the cursor stops blinking, the ring goes, and the next Tab or
    // Shift+Tab continues from here to the neighbouring control (the
    // browser keeps its focus navigation starting point on the blurred
    // editor). Lowest precedence so the editor's own Escape bindings come
    // first — a completion list closes, a selection collapses — and a
    // second Escape then leaves. CodeMirror's built-in hatch kept focus
    // and only armed the next Tab, invisibly.
    Prec.lowest(
      keymap.of([
        {
          key: "Escape",
          run: (view) => {
            view.contentDOM.blur();
            return true;
          },
        },
      ]),
    ),
  ];
}

// Read-only code viewer backed by CodeMirror. Syntax colours reuse the shared
// design-system editor palette (theme.Editor*), matching the Mario JSON viewer.
// Interactions are minimal: a right-click menu with Copy / Copy All / Select All.
// JSON by default; `language`/`highlight` swap in another language (e.g. Rhai
// on the workspace detail script cards) while keeping the same chrome.

interface JsonViewerProps {
  value: string;
  language?: Extension;
  highlight?: HighlightStyle;
  /** Lifts the right-click Copy menu above a stacking context that would
   *  otherwise cover it — a modal's backdrop (pass MODAL_Z_INDEX + 1). */
  floatingZIndex?: number;
}

/** Context menus show every item without an inner scrollbar: the lib's
 *  default "auto" height still caps the list at 400px, which the editor
 *  menus (clipboard + templates + format/indent) exceed. */
export const CONTEXT_MENU_HEIGHT = {
  strategy: "max-height",
  size: 900,
} as const;

export function JsonViewer({
  value,
  language,
  highlight,
  floatingZIndex,
}: JsonViewerProps) {
  const theme = useTheme();
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Drives the right-click menu's Copy item: with nothing selected there's no
  // range to copy, so Copy is disabled while Copy All stays available.
  const [hasSelection, setHasSelection] = useState(false);

  const copySelection = useCallback(() => {
    const view = cmRef.current?.view;
    if (!view) return;
    const sel = view.state.selection.main;
    if (sel.empty) return;
    navigator.clipboard
      .writeText(view.state.sliceDoc(sel.from, sel.to))
      .catch(() => {});
  }, []);

  const copyAll = useCallback(() => {
    navigator.clipboard.writeText(value).catch(() => {});
  }, [value]);

  const contextItems: GenericDropdownItem[] = useMemo(
    () => [
      [
        {
          id: "copy",
          type: "text",
          value: "copy",
          label: "Copy",
          badgeText: KEY_COPY,
          disabled: !hasSelection,
          closeOnSelect: true,
          onClick: copySelection,
        },
        {
          id: "copy-all",
          type: "text",
          value: "copy-all",
          label: "Copy All",
          closeOnSelect: true,
          onClick: copyAll,
        },
      ],
    ],
    [copySelection, copyAll, hasSelection],
  );

  const extensions = useMemo(
    () => [
      ...buildExtensions(theme, { language, highlight }),
      // The fold chevrons in the gutter are mouse-only, so the keyboard
      // gets the same reach on the cursor's line: Enter or Space toggles
      // its fold (the standard fold keymap — Cmd-Alt-[ / ] on a Mac,
      // Ctrl-Shift-[ / ] elsewhere — stays). Safe to take Enter and
      // Space here because the viewer is read-only and neither types.
      Prec.high(
        keymap.of([
          { key: "Enter", run: toggleFold },
          { key: " ", run: toggleFold },
        ]),
      ),
      // Track selection so the context menu only offers itself when there's a
      // range to copy.
      EditorView.updateListener.of((u) => {
        if (u.selectionSet) {
          setHasSelection(!u.state.selection.main.empty);
        }
      }),
    ],
    [theme, language, highlight],
  );

  return (
    <Wrapper ref={containerRef}>
      <CodeMirror
        ref={cmRef}
        value={value}
        extensions={extensions}
        readOnly
        basicSetup={{
          lineNumbers: true,
          foldGutter: false,
          // Read-only viewer: no cursor line should look "focused" on open.
          highlightActiveLine: false,
          highlightActiveLineGutter: false,
          bracketMatching: true,
        }}
      />
      <GenericDropdown
        asContextMenu={containerRef}
        dropdownItems={contextItems}
        width={{ strategy: "fixed", size: 200 }}
        height={CONTEXT_MENU_HEIGHT}
        floatingZIndex={floatingZIndex}
      />
    </Wrapper>
  );
}

const FOCUS = designVariables.ui.semantic;
const FOCUS_COLOUR = designVariables.theme.light.semantic.focusprimary;

// The keyboard focus ring goes around the whole editor — gutter, chevrons
// and code together — not on the content element the global ring rule
// would pick (contenteditable): that element sits beside the gutter inside
// the scroller, so its ring left the line numbers out and its bottom
// stroke scrolled away with the document. Nor as an outline on the editor
// frame: the scroller is a stacking context (position relative, z-index
// 0) and the gutter is sticky inside it at z-index 200 on an opaque
// background, so it painted over the outline's left stroke and its share
// of the top and bottom ones — the ring seemed to start where the code
// does. The ring is a positioned pseudo-element of the editor instead,
// stacked above the scroller and everything in it, on the editor's exact
// box, letting pointer events through. Same gate as every text control:
// the ring shows for keyboard focus only.
const Wrapper = styled.div`
  position: relative;
  height: 100%;

  .cm-content {
    --focus-ring: none;
  }

  :root[data-focus-modality="keyboard"] & .cm-editor.cm-focused::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    border: ${FOCUS.focusstroke}px solid ${FOCUS_COLOUR};
  }
`;

// A keyboard focus ring for a region that scrolls a CodeMirror editor grown
// to its document — the Raw configuration modal's body, the Studio's editor
// panels. The editor's own ring on its frame would end below the visible
// area there, so the ring is drawn over the region instead: the box the
// eye sees, gutter and code together, all four sides on screen. An overlay
// rather than an outline: an inset outline on the scroll host or its
// viewport paints beneath the scrolled content, and one outside the box is
// clipped by the card. It sits above the viewport (which stacks at 0) and
// lets pointer events through, so scrolling and selecting are untouched.
// Inside, the content element's global ring and the editor frame's own ring
// stay off; the overlay is the ring.
export const editorRegionFocusRing = css`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    border: ${FOCUS.focusstroke}px solid transparent;
  }
  :root[data-focus-modality="keyboard"] &:has(.cm-editor.cm-focused)::after {
    border-color: ${FOCUS_COLOUR};
  }

  .cm-content {
    --focus-ring: none;
  }
  /* Doubled selector to outrank the viewer's own frame-ring rule. */
  :root[data-focus-modality="keyboard"] && .cm-editor.cm-focused::after {
    content: none;
  }
`;

export default JsonViewer;
